import { Sigil } from "@/components/Sigil";
import { Logo } from "@/components/Logo";

const INK = { green: "#1f6b3f", pencil: "#5b5f63" };

export function Masthead({ doc }) {
  const right = (
    <div className="text-right">
      <div className="font-mono text-3xl text-[#17232b]" data-testid="paper-ref">{doc.ref}</div>
      <div className="font-mono text-[13px] tracking-wider text-[#17232b] mt-1">FACILITY ARCHIVE</div>
    </div>
  );
  if (doc.family === "military") {
    return (
      <div className="flex items-start justify-between pb-5 border-b-2 border-[#263a52]">
        <div>
          <Logo height={38} />
          <div className="font-mono text-[13px] tracking-wider mt-2">OUTBREAK INQUIRY / RECOVERED MATERIAL</div>
        </div>
        {right}
      </div>
    );
  }
  if (doc.family === "corporate") {
    return (
      <div className="flex items-start justify-between pb-5 border-b-2 border-[#5a4a2f]">
        <div>
          <div className="font-bold text-3xl tracking-[0.12em] text-[#5a4a2f]">ASTER VENN</div>
          <div className="font-mono text-[13px] tracking-wider">PHARMACEUTICALS / PROGRAMME REGISTER COPY</div>
        </div>
        {right}
      </div>
    );
  }
  return (
    <div className="flex items-start justify-between pb-5 border-b-2 border-[#2f5a60]">
      <div>
        <div className="font-bold text-4xl tracking-wide text-[#2f5a60]">SPORE</div>
        <div className="font-mono text-[13px] tracking-wider mt-1">MEDICAL RESEARCH PROGRAMME</div>
      </div>
      {right}
    </div>
  );
}

const rich = (t) => t.split("**").map((s, i) => (i % 2 ? <strong key={i}>{s}</strong> : s));

function Block({ b }) {
  switch (b.type) {
    case "heading":
      return <h4 className="font-bold text-xl mt-6 mb-2">{b.text}</h4>;
    case "subheading":
      return <h5 className="font-bold text-[17px] mt-4 mb-1.5">{b.text}</h5>;
    case "prompt":
      return (
        <div className="prompt-box">
          <div className="prompt-label">{b.label}</div>
          <pre>{b.text}</pre>
        </div>
      );
    case "callout":
      return <div className="my-4 border-2 border-current px-5 py-3 font-bold tracking-wide uppercase text-[19px]">{b.text}</div>;
    case "note":
      return (
        <p className="font-hand text-[22px] leading-snug my-3 pl-4 border-l-2" style={{ color: INK[b.ink] || "#23344a", borderColor: INK[b.ink] || "#23344a" }}>
          {b.text}
        </p>
      );
    case "list":
      return (
        <ul className={`list-disc pl-6 space-y-1.5 ${b.tight ? "my-1" : "my-2"}`}>
          {b.items.map((it) => <li key={it}>{rich(it)}</li>)}
        </ul>
      );
    case "checklist":
      return (
        <ul className="space-y-3 my-3">
          {b.items.map((it) => <li key={it} className="flex gap-4"><span className="check-box" aria-hidden />{it}</li>)}
        </ul>
      );
    case "table":
      return (
        <table className={b.tall ? "tall" : ""}>
          <thead><tr>{b.headers.map((h) => <th key={h}>{h}</th>)}</tr></thead>
          <tbody>{b.rows.map((r, ri) => <tr key={ri}>{r.map((c, i) => <td key={i}>{c}</td>)}</tr>)}</tbody>
        </table>
      );
    default:
      return <p className="my-3">{rich(b.text)}</p>;
  }
}

const span = (doc, part) => (part ? part.range : [0, doc.blocks.length]);
const wrap = (part, i, el) => (part ? <div key={i} data-bi={i} className="flow-block">{el}</div> : el);

function CultLeaflet({ doc, testid, children, part }) {
  const [s, e] = span(doc, part);
  const first = !part || part.n === 1;
  const last = !part || part.n === part.total;
  return (
    <article className="paper-cult max-w-[820px] mx-auto px-14 py-12 rise" data-testid={testid}>
      {first && (
        <div className="flex flex-col items-center text-center">
          <Sigil size={130} color="#0b0a07" stroke={5} />
          <div className="font-mono text-[13px] tracking-[0.3em] mt-4 text-[#4a4130]">{doc.ref} · RECOVERED FROM THE OLD CHAPEL</div>
          <h3 className="font-fell text-5xl mt-3">{doc.title}</h3>
        </div>
      )}
      {doc.blocks.slice(s, e).map((b, j) => wrap(part, s + j, <CultBlock key={s + j} b={b} />))}
      {last && children}
      {last && <div className="flex justify-end mt-6"><span className="stamp-cult font-mono text-sm tracking-[0.2em] border-2 border-[#3a2f1c] px-4 py-1.5 rotate-[-4deg] inline-block">{doc.stamp}</span></div>}
    </article>
  );
}

function CultBlock({ b }) {
        if (b.type === "verse") return <p className="font-fell italic text-[26px] leading-relaxed text-center mt-8">{b.text}</p>;
        if (b.type === "callout") return <p className="font-fell text-[22px] text-center mt-6 tracking-wide">{b.text}</p>;
        if (b.type === "attendance")
          return (
            <div className="mt-8 border-t border-[#8a7a55] pt-5">
              <div className="font-mono text-[13px] tracking-[0.25em] text-[#4a4130] mb-3">THOSE RECEIVED</div>
              <div className="grid grid-cols-3 gap-x-8 gap-y-3 font-hand text-2xl">
                {b.names.map(([n, scratched], j) => (
                  <span key={j} className={scratched ? "text-[#2a241a]" : "text-[#16130c] font-bold"}>
                    {scratched ? <span className="inline-block w-28 h-5 bg-[#2a241a] rotate-[-2deg] rounded-sm" /> : n}
                  </span>
                ))}
              </div>
            </div>
          );
        return <Block b={b} />;
}

export function Paper({ doc, testid = "paper-document", children, part, aside = null }) {
  if (!doc) return null;
  if (doc.family === "cult") return <CultLeaflet doc={doc} testid={testid} part={part}>{children}</CultLeaflet>;
  const [s, e] = span(doc, part);
  const first = !part || part.n === 1;
  const last = !part || part.n === part.total;
  const meta = first ? doc.metadata || [] : [];
  return (
    <article className={`paper ${doc.paper || ""} ${doc.family === "military" ? "mil" : ""} max-w-[900px] mx-auto px-14 pt-12 pb-8 rise`} data-testid={testid}>
      <Masthead doc={doc} />
      <h3 className="font-bold text-[40px] leading-tight mt-8" data-testid="paper-title">{doc.title}{first || doc.title.includes("(continued)") ? "" : " (continued)"}</h3>
      {first && doc.subtitle && <div className="text-[#5f6f78] text-lg mt-1">{doc.subtitle}</div>}
      {meta.length > 0 && (
        <div className="grid grid-cols-2 gap-x-10 gap-y-3 mt-6 pb-5 border-b border-[#d6d9d8]">
          {meta.map(([k, v]) => (
            <div key={k}>
              <div className="meta-label">{k}</div>
              <div>{v}</div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-4">{doc.blocks.slice(s, e).map((b, j) => wrap(part, s + j, <Block key={s + j} b={b} />))}</div>
      {last && children}
      {last && (
        <div className="flex items-center justify-between gap-6 mt-10 pt-5 border-t border-[#d6d9d8] paper-sign">
          <span className="font-mono italic text-[17px]">{doc.signature}</span>
          {aside}
          <span className="stamp shrink-0">{doc.stamp}</span>
        </div>
      )}
      <div className="flex justify-between mt-10 font-mono text-[12px] tracking-wider text-[#6b7a82] paper-index">
        <span>RECOVERY INDEX {doc.ref}</span>
        <span>{part && part.total > 1 ? `PAGE ${part.n} OF ${part.total}` : "FACILITY TIME / INCIDENT RECORD"}</span>
      </div>
    </article>
  );
}
